import { useEffect, useMemo, useRef, useState } from "react";
import Header from "../../../components/Header";
import LoadingOverlay from "../../../components/LoadingOverlay";
import ReportFiltersCard, {
    ReportFilterField,
} from "../../../components/reports/ReportFiltersCard";
import Sidebar from "../../../components/Sidebar";
import { getEmployeesWorkloadReport } from "../../../services/analytics/getEmployeesWorkload";
import EmployeeWorkloadTable from "./components/EmployeeWorkloadTable";
import { getEmployees } from "../../../services/getEmployees";
import Select from "react-select";
import { getServiceDepartments } from "../../../services/service/getServiceDepartments";
import { getTeamRoles } from "../../../services/teams/getTeamRoles";
import { getWorkloadByTeams } from "../../../services/analytics/getWorkloadByTeams";
import WorkloadReportByTeams from "./components/WorkloadReportByTeams";
import { getWorkloadByDepartments } from "../../../services/analytics/getWorkloadByDepartments";
import WorkloadReportByDepartments from "./components/WorkloadReportByDepartments";
import TeamRoleBadge from "../../../components/teamRoles/TeamRoleBadge";
import {
    buildEmployeeOptions,
    normalizeWorkloadTeamRoles,
} from "./workloadFilterUtils";

const SELECT_INITIALIZATION = [
    {
        value: "00000000-0000-0000-0000-000000000000",
        label: "All",
        is_showing: true,
    },
];

const VIEW_BY_OPTIONS = [
    { value: "employees", label: "Employees" },
    { value: "teams", label: "Teams" },
    { value: "departments", label: "Departments" },
];

const CLIENT_TYPE_OPTIONS = [
    { value: null, label: "All" },
    { value: 1, label: "Individual" },
    { value: 2, label: "Business" },
];

const getInitialFilters = () => ({
    employeeId: SELECT_INITIALIZATION[0].value,
    departmentId: SELECT_INITIALIZATION[0].value,
    teamRoleId: SELECT_INITIALIZATION[0].value,
    clientType: null,
    group: "employees",
});

const WorkloadReport = () => {
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const [clientsByEmployee, setClientsByEmployee] = useState([]);
    const [clientsByTeam, setClientsByTeam] = useState([]);
    const [clientsByDepartment, setClientsByDepartment] = useState([]);

    const [employees, setEmployees] = useState(SELECT_INITIALIZATION);
    const [departments, setDepartments] = useState(SELECT_INITIALIZATION);
    const [teamRoles, setTeamRoles] = useState(SELECT_INITIALIZATION);

    const [selectedEmployee, setSelectedEmployee] = useState(
        SELECT_INITIALIZATION[0],
    );
    const [selectedDepartment, setSelectedDepartment] = useState(
        SELECT_INITIALIZATION[0],
    );
    const [selectedTeamRole, setSelectedTeamRole] = useState(
        SELECT_INITIALIZATION[0],
    );
    const [selectedClientType, setSelectedClientType] = useState(
        CLIENT_TYPE_OPTIONS[0],
    );
    const [selectedGroup, setSelectedGroup] = useState("employees");
    const [appliedFilters, setAppliedFilters] = useState(getInitialFilters);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const resultsSectionRef = useRef(null);
    const shouldScrollToResultsRef = useRef(false);
    const reportRequestSequenceRef = useRef(0);

    const selectedEmployeeOption = useMemo(
        () =>
            employees.find((employee) => employee.value === selectedEmployee.value) ||
            employees[0] ||
            SELECT_INITIALIZATION[0],
        [employees, selectedEmployee],
    );

    const selectedDepartmentOption = useMemo(
        () =>
            departments.find(
                (department) => department.value === selectedDepartment.value,
            ) ||
            departments[0] ||
            SELECT_INITIALIZATION[0],
        [departments, selectedDepartment],
    );

    const selectedTeamRoleOption = useMemo(
        () =>
            teamRoles.find(
                (teamRole) => teamRole.value === selectedTeamRole.value,
            ) ||
            teamRoles[0] ||
            SELECT_INITIALIZATION[0],
        [teamRoles, selectedTeamRole],
    );

    const selectedGroupOption = useMemo(
        () =>
            VIEW_BY_OPTIONS.find((option) => option.value === selectedGroup) ||
            VIEW_BY_OPTIONS[0],
        [selectedGroup],
    );

    useEffect(() => {
        fetchFilterCatalogs();
    }, []);

    useEffect(() => {
        let isMounted = true;
        const requestSequence = ++reportRequestSequenceRef.current;

        async function loadReport() {
            try {
                setIsLoading(true);
                setError(null);

                let reportData;
                if (appliedFilters.group === "employees") {
                    reportData = await fetchReportByEmployees(appliedFilters);
                } else if (appliedFilters.group === "teams") {
                    reportData = await fetchReportByTeams(appliedFilters);
                } else if (appliedFilters.group === "departments") {
                    reportData = await fetchReportByDepartments(appliedFilters);
                }

                if (
                    !isMounted ||
                    requestSequence !== reportRequestSequenceRef.current
                ) {
                    return;
                }

                if (appliedFilters.group === "employees") {
                    setClientsByEmployee(reportData);
                } else if (appliedFilters.group === "teams") {
                    setClientsByTeam(reportData);
                } else if (appliedFilters.group === "departments") {
                    setClientsByDepartment(reportData);
                }
            } catch (err) {
                if (
                    !isMounted ||
                    requestSequence !== reportRequestSequenceRef.current
                ) {
                    return;
                }
                setError(err);
            } finally {
                if (
                    !isMounted ||
                    requestSequence !== reportRequestSequenceRef.current
                ) {
                    return;
                }
                setIsLoading(false);
            }
        }

        loadReport();

        return () => {
            isMounted = false;
        };
    }, [appliedFilters]);

    const fetchFilterCatalogs = async () => {
        const [employeesResponse, departmentsResponse, teamRolesResponse] =
            await Promise.all([
                getEmployees(),
                getServiceDepartments(),
                getTeamRoles(),
            ]);

        const normalizedTeamRoles = normalizeWorkloadTeamRoles(
            teamRolesResponse?.data?.teamRoles,
        );

        if (employeesResponse?.data?.employees) {
            setEmployees([
                SELECT_INITIALIZATION[0],
                ...buildEmployeeOptions(
                    employeesResponse.data.employees,
                    normalizedTeamRoles,
                ),
            ]);
        }

        const departmentsArray = [];
        if (departmentsResponse?.data?.success) {
            departmentsResponse.data.departments.forEach((department) => {
                departmentsArray.push({
                    value: department.id,
                    label: department.name,
                    is_showing: true,
                });
            });
            setDepartments([SELECT_INITIALIZATION[0], ...departmentsArray]);
        }

        setTeamRoles([
            SELECT_INITIALIZATION[0],
            ...normalizedTeamRoles.map((teamRole) => ({
                value: teamRole.id,
                label: teamRole.name,
                colorHex: teamRole.colorHex,
                is_showing: true,
            })),
        ]);
    };

    const fetchReportByEmployees = async (nextFilters) => {
        const response = await getEmployeesWorkloadReport({
            employeeId: nextFilters.employeeId,
            departmentId: nextFilters.departmentId,
            teamRoleId: nextFilters.teamRoleId,
            clientType: nextFilters.clientType,
            includeInactive: false,
        });

        if (response?.data?.success) {
            return response.data.employeesWorkload;
        } else {
            throw new Error(
                response?.data?.message ||
                    response?.message ||
                    "Failed to fetch Workload Report",
            );
        }
    };

    const fetchReportByTeams = async (nextFilters) => {
        const response = await getWorkloadByTeams({
            employeeId: nextFilters.employeeId,
            departmentId: nextFilters.departmentId,
            teamRoleId: nextFilters.teamRoleId,
            clientType: nextFilters.clientType,
            includeInactive: false,
        });

        if (response?.data?.success) {
            return response.data.workloadByTeams;
        } else {
            throw new Error(
                response?.data?.message ||
                    response?.message ||
                    "Failed to fetch workload by teams",
            );
        }
    };

    const fetchReportByDepartments = async (nextFilters) => {
        const response = await getWorkloadByDepartments({
            employeeId: nextFilters.employeeId,
            departmentId: nextFilters.departmentId,
            teamRoleId: nextFilters.teamRoleId,
            clientType: nextFilters.clientType,
            includeInactive: false,
        });

        if (response?.data?.success) {
            return response.data.workloadByDepartments;
        } else {
            throw new Error(
                response?.data?.message ||
                    response?.message ||
                    "Failed to fetch workload by departments",
            );
        }
    };

    const onChangEmployee = (e) => {
        setSelectedEmployee(e);
    };

    const onChangeDepartment = (e) => {
        setSelectedDepartment(e);
    };

    const onChangeTeamRole = (option) => {
        setSelectedTeamRole(option);
    };

    const onChangeGroup = (option) => {
        const nextGroup = option?.value || VIEW_BY_OPTIONS[0].value;
        setSelectedGroup(nextGroup);
    };

    const handleApplyFilters = () => {
        shouldScrollToResultsRef.current = true;
        setAppliedFilters({
            employeeId: selectedEmployee.value,
            departmentId: selectedDepartment.value,
            teamRoleId: selectedTeamRole.value,
            clientType: selectedClientType?.value ?? null,
            group: selectedGroup,
        });
    };

    const handleResetFilters = () => {
        setSelectedEmployee(SELECT_INITIALIZATION[0]);
        setSelectedDepartment(SELECT_INITIALIZATION[0]);
        setSelectedTeamRole(SELECT_INITIALIZATION[0]);
        setSelectedClientType(CLIENT_TYPE_OPTIONS[0]);
        setSelectedGroup(VIEW_BY_OPTIONS[0].value);
        shouldScrollToResultsRef.current = true;
        setAppliedFilters(getInitialFilters());
    };

    useEffect(() => {
        if (isLoading || !shouldScrollToResultsRef.current) {
            return;
        }

        if (resultsSectionRef.current) {
            resultsSectionRef.current.scrollIntoView({
                behavior: "smooth",
                block: "start",
            });
        }

        shouldScrollToResultsRef.current = false;
    }, [
        clientsByDepartment,
        clientsByEmployee,
        clientsByTeam,
        isLoading,
        appliedFilters.group,
    ]);

    return (
        <>
            <LoadingOverlay isLoading={isLoading} />
            <section>
                <div className="flex h-screen overflow-hidden">
                    <Sidebar
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                        <Header
                            sidebarOpen={sidebarOpen}
                            setSidebarOpen={setSidebarOpen}
                        />

                        <main>
                            <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                                <div className="flex flex-col items-start max-w-3xl">
                                    <h1 className="text-2xl font-bold md:text-3xl text-slate-800">
                                        Workload Report
                                    </h1>
                                </div>

                                <div
                                    className="grid grid-cols-1 gap-6 lg:grid-cols-12 lg:items-start"
                                    data-testid="workload-filter-layout"
                                >
                                    <div className="min-w-0 lg:col-span-3">
                                        <ReportFiltersCard
                                            title="Report View"
                                            fieldsClassName="grid grid-cols-1"
                                        >
                                            <ReportFilterField label="View By">
                                                <Select
                                                    onChange={onChangeGroup}
                                                    classNamePrefix="select"
                                                    isSearchable={false}
                                                    value={selectedGroupOption}
                                                    options={VIEW_BY_OPTIONS}
                                                    name="viewBy"
                                                    isDisabled={isLoading}
                                                />
                                            </ReportFilterField>
                                        </ReportFiltersCard>
                                    </div>

                                    <div className="min-w-0 lg:col-span-9">
                                        <ReportFiltersCard
                                            title="Filters"
                                            actions={
                                                <div className="flex flex-wrap items-center justify-end gap-2">
                                                    <button
                                                        type="button"
                                                        onClick={handleResetFilters}
                                                        disabled={isLoading}
                                                        className="inline-flex items-center px-4 py-2 text-sm font-medium transition bg-white border rounded-lg shadow-sm text-slate-700 border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        Reset
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={handleApplyFilters}
                                                        disabled={isLoading}
                                                        className="inline-flex items-center px-4 py-2 text-sm font-medium text-white transition bg-teal-500 rounded-lg shadow-sm hover:bg-teal-600 disabled:cursor-not-allowed disabled:opacity-60"
                                                    >
                                                        Apply Filters
                                                    </button>
                                                </div>
                                            }
                                            fieldsClassName="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
                                        >
                                            <ReportFilterField label="Employee">
                                                <Select
                                                    onChange={onChangEmployee}
                                                    classNamePrefix="select"
                                                    isSearchable={true}
                                                    value={selectedEmployeeOption}
                                                    options={employees}
                                                    name="employees"
                                                    isDisabled={isLoading}
                                                />
                                            </ReportFilterField>

                                            <ReportFilterField label="Department">
                                                <Select
                                                    onChange={onChangeDepartment}
                                                    classNamePrefix="select"
                                                    isSearchable={true}
                                                    value={selectedDepartmentOption}
                                                    options={departments}
                                                    name="departments"
                                                    isDisabled={isLoading}
                                                />
                                            </ReportFilterField>

                                            <ReportFilterField label="Team Role">
                                                <Select
                                                    onChange={onChangeTeamRole}
                                                    classNamePrefix="select"
                                                    isSearchable={true}
                                                    value={selectedTeamRoleOption}
                                                    options={teamRoles}
                                                    name="teamRoles"
                                                    isDisabled={isLoading}
                                                    formatOptionLabel={(option) =>
                                                        option.value ===
                                                        SELECT_INITIALIZATION[0]
                                                            .value ? (
                                                            option.label
                                                        ) : (
                                                            <TeamRoleBadge
                                                                name={option.label}
                                                                colorHex={
                                                                    option.colorHex
                                                                }
                                                            />
                                                        )
                                                    }
                                                />
                                            </ReportFilterField>

                                            <ReportFilterField label="Client Type">
                                                <Select
                                                    onChange={setSelectedClientType}
                                                    classNamePrefix="select"
                                                    isSearchable={false}
                                                    value={selectedClientType}
                                                    options={CLIENT_TYPE_OPTIONS}
                                                    name="clientType"
                                                    isDisabled={isLoading}
                                                />
                                            </ReportFilterField>

                                        </ReportFiltersCard>
                                    </div>
                                </div>

                                {error && (
                                    <div className="p-3 mt-6 text-sm text-red-700 border border-red-200 rounded-lg shadow-sm bg-red-50">
                                        {error.message ||
                                            "Error loading Workload Report."}
                                    </div>
                                )}
                            </div>

                            <div
                                ref={resultsSectionRef}
                                className="w-full px-4 pb-8 mt-6 sm:px-6 lg:px-8"
                            >
                                {appliedFilters.group === "employees" && (
                                    <div>
                                        <EmployeeWorkloadTable
                                            data={clientsByEmployee}
                                            appliedFilters={appliedFilters}
                                            className="w-full"
                                        />
                                    </div>
                                )}

                                {appliedFilters.group === "teams" && (
                                    <div>
                                        <WorkloadReportByTeams
                                            data={clientsByTeam}
                                            appliedFilters={appliedFilters}
                                            className="w-full"
                                        />
                                    </div>
                                )}

                                {appliedFilters.group === "departments" && (
                                    <div>
                                        <WorkloadReportByDepartments
                                            data={clientsByDepartment}
                                            appliedFilters={appliedFilters}
                                            className="w-full"
                                        />
                                    </div>
                                )}
                            </div>
                        </main>
                    </div>
                </div>
            </section>
        </>
    );
};

export default WorkloadReport;
